import { useTranslation } from "react-i18next";
import PageShell, { Breadcrumb } from "@/components/feature/PageShell";

const deptData = [
  { titleKey: "about.org.council", descKey: "about.org.council.desc", membersKeys: ["about.org.council.m0", "about.org.council.m1", "about.org.council.m2", "about.org.council.m3"], color: "primary" },
  { titleKey: "about.org.board", descKey: "about.org.board.desc", membersKeys: ["about.org.board.m0", "about.org.board.m1", "about.org.board.m2"], color: "accent" },
  { titleKey: "about.org.expert", descKey: "about.org.expert.desc", membersKeys: ["about.org.expert.m0", "about.org.expert.m1", "about.org.expert.m2"], color: "secondary" },
];

const departmentsKeys = [0, 1, 2, 3, 4, 5];

export default function AboutOrganization() {
  const { t } = useTranslation();

  return (
    <PageShell>
      {/* Hero */}
      <section className="relative pt-24 pb-20 bg-gradient-to-b from-secondary-900 to-secondary-800 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://readdy.ai/api/search-image?query=Modern%20architectural%20blueprint%20style%20pattern%20with%20interconnected%20nodes%20and%20flowing%20lines%2C%20dark%20jade%20green%20background%2C%20organizational%20network%20visualization%2C%20clean%20geometric%20design%2C%20corporate%20structure%20aesthetic%2C%20no%20text%2C%20no%20people&width=1600&height=600&seq=about-org-hero-01&orientation=landscape')] bg-cover bg-center opacity-15"></div>
        <div className="relative w-full max-w-7xl mx-auto px-4 md:px-8">
          <Breadcrumb items={[{ label: t("nav.about") }, { label: t("about.org.title") }]} />
          <div className="mt-6 max-w-3xl">
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-background-50 leading-tight">
              {t("about.org.title")}
            </h1>
            <p className="mt-4 text-lg text-background-100/80 leading-relaxed">
              {t("about.org.subtitle")}
            </p>
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="py-16 md:py-20">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-8">
          <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-8">{t("about.org.leadership")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {deptData.map((dept, i) => {
              const colorMap: Record<string, { bg: string; border: string; icon: string; badge: string; heading: string }> = {
                primary: { bg: "bg-primary-50", border: "border-primary-200", icon: "bg-primary-600", badge: "bg-primary-100 text-primary-700", heading: "text-primary-800" },
                accent: { bg: "bg-accent-50", border: "border-accent-200", icon: "bg-accent-500", badge: "bg-accent-100 text-accent-700", heading: "text-accent-800" },
                secondary: { bg: "bg-secondary-50", border: "border-secondary-200", icon: "bg-secondary-500", badge: "bg-secondary-100 text-secondary-700", heading: "text-secondary-800" },
              };
              const c = colorMap[dept.color];
              return (
                <div key={i} className={`${c.bg} border ${c.border} rounded-lg p-6`}>
                  <div className={`w-12 h-12 flex items-center justify-center rounded-full ${c.icon} text-background-50 mb-4`}>
                    <i className="ri-building-2-line text-xl"></i>
                  </div>
                  <h3 className={`font-heading font-bold text-lg ${c.heading} mb-3`}>{t(dept.titleKey)}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">{t(dept.descKey)}</p>
                  <div className="space-y-1.5">
                    {dept.membersKeys.map((mk, j) => (
                      <span key={j} className={`inline-block ${c.badge} text-xs font-medium px-2.5 py-1 rounded-full mr-2 mb-1 whitespace-nowrap`}>
                        {t(mk)}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Departments - hidden per request, kept for records */}
      {/* <section className="py-16 md:py-20 bg-background-100">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-8">
          <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-2">{t("about.org.departments")}</h2>
          <p className="text-foreground-500 mb-8">{t("about.org.departments.desc")}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {departmentsKeys.map((i) => (
              <div key={i} className="bg-background-50 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-2 h-2 rounded-full bg-primary-500 flex-shrink-0"></div>
                  <h3 className="font-heading font-semibold text-foreground-900">{t(`about.org.dept.${i}.name`)}</h3>
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed">{t(`about.org.dept.${i}.desc`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Org Chart Visual */}
      <section className="py-16 md:py-20">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-8">
          <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">{t("about.org.chart")}</h2>
          <div className="flex flex-col items-center gap-4">
            {/* Top level */}
            <div className="bg-primary-600 text-background-50 rounded-lg px-8 py-4 text-center">
              <span className="font-heading font-bold text-lg">{t("about.org.chart.council")}</span>
              <p className="text-xs text-background-100/80 mt-1">{t("about.org.chart.council.sub")}</p>
            </div>
            <div className="w-px h-8 bg-foreground-300"></div>
            <div className="bg-accent-500 text-background-50 rounded-lg px-8 py-4 text-center">
              <span className="font-heading font-bold text-lg">{t("about.org.chart.board")}</span>
              <p className="text-xs text-background-100/80 mt-1">{t("about.org.chart.board.sub")}</p>
            </div>
            <div className="flex items-center gap-0">
              <div className="w-32 h-px bg-foreground-300"></div>
              <div className="w-px h-8 bg-foreground-300"></div>
              <div className="w-32 h-px bg-foreground-300"></div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 w-full max-w-3xl">
              {departmentsKeys.map((i) => (
                <div key={i} className="bg-background-100 rounded-lg p-4 text-center">
                  <p className="font-medium text-sm text-foreground-900">{t(`about.org.dept.${i}.name`)}</p>
                </div>
              ))}
            </div>
            <div className="w-px h-8 bg-foreground-300"></div>
            <div className="bg-secondary-500 text-background-50 rounded-lg px-8 py-4 text-center">
              <span className="font-heading font-bold text-lg">{t("about.org.chart.expert")}</span>
              <p className="text-xs text-background-100/80 mt-1">{t("about.org.chart.expert.sub")}</p>
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
}